import { defineStore } from "pinia";
import { computed, reactive, ref } from "vue";
import type { Coil } from "../domain/coil";
import { useEntityStore } from "../../common/composables/entityStore";

export const useCoilStore = defineStore('coilStore', () => {

  const {
    add,
    clear,
    getById,
    merge,
    remove,
    list,
    changed,
    update
  } = useEntityStore<Coil>({
    eventName: 'coil-changed',
    getEntityId: (e) => e.id,
    insertEntity: (entity) => {
      addStorageAreaToCoil(entity.storageAreaId, entity.id);
    },
    beforeUpdateEntity: (entity) => {
      removeStorageAreaToCoil(entity.storageAreaId, entity.id);
    },
    updateEntity: (dest, src) => {
      dest.depth = src.depth;
      dest.diameter = src.diameter;
      dest.level = src.level;
      dest.name = src.name;
      dest.position = src.position;
      dest.productId = src.productId;
      dest.storageAreaId = src.storageAreaId;
    },
    afterUpdateEntity: (entity) => {
      addStorageAreaToCoil(entity.storageAreaId, entity.id);
    },
    removeEntity: (entity) => {
      removeStorageAreaToCoil(entity.storageAreaId, entity.id);
    }
  });

  const storageAreaToCoilIds = new Map<string, Set<string>>();

  function getByStorageAreaId(storageAreaId: string) : Coil[] {
    const coilIds = storageAreaToCoilIds.get(storageAreaId);
    const result: Coil[] = [];
    if (!coilIds || coilIds.size === 0) return result;

    for (const id of coilIds) {
      const coil = getById(id);
      if (!coil) continue;
      result.push(coil);
    }

    return result;
  }

  function addStorageAreaToCoil(storageAreaId: string|null, coilId: string) {
    if (!storageAreaId) return;
    let shapeIds = storageAreaToCoilIds.get(storageAreaId);
    if (!shapeIds) {
      shapeIds = new Set();
      storageAreaToCoilIds.set(storageAreaId, shapeIds);
    }
    shapeIds.add(coilId);
  }

  function removeStorageAreaToCoil(storageAreaId: string|null, coilId: string) {
    if (!storageAreaId) return;
    const coilIds = storageAreaToCoilIds.get(storageAreaId);
    if (!coilIds) return;
    coilIds.delete(coilId);
    if (coilIds.size > 0) return;
    storageAreaToCoilIds.delete(storageAreaId);
  }

  return {
    getById,
    add,
    remove,
    getByStorageAreaId,
    merge,
    clear,
    coils: computed(() => list.value),
    changed,
    update
  };
});

export interface MoveCoil {
  storageAreaId: string;
  level: number;
  position: number;
}
